/**
 * Copyright IBM Corp. 2021
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */
import memo from "memoize-one";
import debounce from "lodash/debounce";
import TriFloorPlanPlugin from "./TriFloorPlanPlugin.js";
import TriFloorPlanUtils from "../util/TriFloorPlanUtils.js";
import TriFloorPlanAPI from "../api/TriFloorPlanAPI.js";

export default class TriPinPlugin extends TriFloorPlanPlugin {
  static type = "pin";
  static PIN_TYPE = "type";

  constructor() {
    super();
    this.idAttribute = TriPinPlugin.NAME_PROPERTY;
    this.calculatedScale = 1;
    this.pinnedSpaces = [];
  }

  get pinSize() {
    return this.props.pinSize != null ? this.props.pinSize : 32;
  }

  get className() {
    return this.props.className != null ? this.props.className : "pinned";
  }

  get selectedClassName() {
    return this.props.selectedClassName != null
      ? this.props.selectedClassName
      : "selected";
  }

  pluginDidUpdate() {
    const { floorPlanLoaded, enabled } = this.props;
    if (!floorPlanLoaded || !enabled) {
      this.stop();
      return;
    }
    this.start();
  }

  pluginDidRemove() {
    this.stop();
  }

  get pins() {
    if (this.props.pins != null) {
      return !Array.isArray(this.props.pins)
        ? [this.props.pins]
        : this.props.pins;
    }
    return [];
  }

  memoizedParsedIcon = memo((icon) => {
    if (icon instanceof Object) {
      const parsedIcons = {};
      Object.entries(icon).forEach(([pinType, svg], index) => {
        parsedIcons[pinType] = this.parseIcon(svg);
        if (index === 0) {
          parsedIcons.default = parsedIcons[pinType];
        }
      });
      return parsedIcons;
    } else {
      return { default: this.parseIcon(icon) };
    }
  });

  parseIcon(svg) {
    return new DOMParser().parseFromString(svg, "application/xml")
      .documentElement;
  }

  getParsedIcon(pinType) {
    if (this.props.icon != null) {
      const parsedIcons = this.memoizedParsedIcon(this.props.icon);
      const icon = parsedIcons[pinType];
      return icon != null ? icon : parsedIcons.default;
    }
    return null;
  }

  floorPlanDidResize() {
    this.updateSvgIcons();
  }

  updateSvgIcons = debounce(() => {
    const {
      triFloorPlan,
      preservePinSizeRatio,
      enabled,
      floorPlanLoaded,
    } = this.props;
    const svgViewPort = triFloorPlan.svgViewPort;
    if (
      !enabled ||
      !floorPlanLoaded ||
      !svgViewPort.width ||
      !svgViewPort.height
    ) {
      return;
    }
    const calculatedScale = TriFloorPlanUtils.calcSvgScale(
      triFloorPlan.svgElement,
      triFloorPlan.svgViewPort,
      preservePinSizeRatio
    );

    if (calculatedScale === this.calculatedScale) return;
    this.calculatedScale = calculatedScale;

    this.pinnedSpaces.forEach((space) => {
      const pinIcon = space.pinIcon;
      this.scalePinIconSizeAndCenter(
        pinIcon,
        space,
        calculatedScale,
        this.pinSize,
        this.centerPoints
      );
    });
  }, 20);

  async start() {
    const { triFloorPlan } = this.props;
    this.addSvgTapListener();
    this.centerPoints = await TriFloorPlanAPI.getCenterPoints(
      triFloorPlan.floorPlanId
    );
    if (!triFloorPlan.svgElement) {
      return;
    }

    // Is floorplan attached?
    if( triFloorPlan.props.spaceLayer )
    {
      this.idAttribute = TriPinPlugin.EMELEMENT_PROPERTY;
    }
    else
    {
      this.idAttribute = TriPinPlugin.NAME_PROPERTY;
    }

    if(
      this.props.pins !== this.lastPins ||
      this.props.icon !== this.lastIcon
    ) {
      this.removeAllPins(this.lastPins);
      this.addAllPins();
      this.lastPins = this.props.pins;
      this.lastIcon = this.props.icon;
    }
    if (this.props.selected !== this.lastSelected) {
      this.updateSelected(this.props.selected);
      this.lastSelected = this.props.selected;
    }
    if (this.props.svgViewBox !== this.lastSvgViewBox) {
      this.updateSvgIcons();
      this.lastSvgViewBox = this.props.svgViewBox;
    }
  }

  updateSelected(selected) {
    if (selected != null) {
      if (
        !this.lastSelected ||
        TriFloorPlanUtils.getPropValue(
          this.lastSelected,
          TriPinPlugin.SPACE_ID
        ) !== TriFloorPlanUtils.getPropValue(selected, TriPinPlugin.SPACE_ID)
      ) {
        this.deselectSvgPin(this.lastSelected);
        this.selectSvgPin(selected);
      }
    } else if (this.lastSelected != null) {
      this.deselectSvgPin(this.lastSelected);
    }
  }

  stop() {
    if (this.props.floorPlanLoaded) {
      this.removeSvgTapListener();
      this.removeAllPins(this.lastPins);
    } else {
      this.calculatedScale = 1;
      this.pinnedSpaces = [];
    }
  }

  addAllPins() {
    const { triFloorPlan, preservePinSizeRatio } = this.props;
    this.calculatedScale = TriFloorPlanUtils.calcSvgScale(
      triFloorPlan.svgElement,
      triFloorPlan.svgViewPort,
      preservePinSizeRatio
    );
    const pins = this.pins;
    if (pins && pins.length > 0 && this.props.icon != null) {
      pins.forEach((pin) => {
        this.addPin(pin);
      });
    }
  }

  addPin( pin )
  {
    const { triFloorPlan } = this.props;
    const space = triFloorPlan.attachedMap.get(
      TriFloorPlanUtils.getPropValue(pin, TriPinPlugin.SPACE_ID)
    );
    if (space) {
      if (!space.pins) {
        space.pins = [];
      }

      this.addPinToSpace(space, pin);
      // handle multiple pins scenario todo later
    } else {
      console.log(
        `Space not found for space id '${TriFloorPlanUtils.getPropValue(
          pin,
          TriPinPlugin.SPACE_ID
        )}'.`
      );
    }
  }

  removeAllPins(pins) {
    if (pins && pins.length > 0) {
      const pinsToRemoveLength = pins.length;
      for (let i = 0; i < pinsToRemoveLength; i++) {
        this.removePin(pins[i]);
      }
    }
  }

  removePin(pin) {
    const space = this.pinnedSpaces.find(
      (pinnedSpace) =>
        pinnedSpace.getAttribute( this.idAttribute ) ===
        TriFloorPlanUtils.getPropValue(pin, TriPinPlugin.SPACE_ID)
    );
    if (space != null) {
      if (space && space.pins && space.pins.length > 0) {
        const spacePinIndex = space.pins.findIndex(
          (spacePin) =>
            TriFloorPlanUtils.getPropValue(spacePin, TriPinPlugin.SPACE_ID) ===
            TriFloorPlanUtils.getPropValue(pin, TriPinPlugin.SPACE_ID)
        );
        if (spacePinIndex !== -1) {
          space.pins.splice(spacePinIndex, 1);
          if (space.pins.length === 0) {
            this.unpinSpace(space);
          } else {
            // handle multiple pins scenario todo later
          }
        }
      }
    }
  }

  addPinToSpace( space, pin ) {
    this.addSvgPinGroup(space);
    this.addSvgPinIcon(space, pin);
    space.pins.push(pin);
    space.counter = space.counter ? space.counter + 1 : 1;
  }

  addSvgPinGroup(space) {
    const svgPinGroup = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "g"
    );

    svgPinGroup.id = "svgPinGroup" + space.getAttribute( this.idAttribute );
    this.props.triFloorPlan.svgElement.appendChild(svgPinGroup);
    space.svgPinGroup = svgPinGroup;
  }

  addSvgPinIcon(space, pin) {
    const pinIcon = this.getParsedIcon(pin[TriPinPlugin.PIN_TYPE]).cloneNode(
      true
    );
    const triRecordId = space.getAttribute( this.idAttribute );
    if (pinIcon) {
      pinIcon.setAttribute("id", space.svgPinGroup.id + "Icon");
      pinIcon.setAttribute("class", this.className);
      TriFloorPlanUtils.setTriRecordIdOnPin( pinIcon, triRecordId, this.idAttribute );

      this.scalePinIconSizeAndCenter(
        pinIcon,
        space,
        this.calculatedScale,
        this.pinSize,
        this.centerPoints,
        pin.location,
      );

      space.svgPinGroup.appendChild(pinIcon);
      space.pinIcon = pinIcon;
      space.svgElement = this.props.triFloorPlan.svgElement;
      this.pinnedSpaces.push(space);
    }
  }

  unpinSpace(space) {
    if (
      space.svgPinGroup &&
      space.svgElement === this.props.triFloorPlan.svgElement
    ) {
      this.props.triFloorPlan.svgElement.removeChild(space.svgPinGroup);
    }

    space.svgPinGroup = null;
    space.pinIcon = null;
    space.counter = 0;
    space.pins = null;
    const index = this.pinnedSpaces.indexOf(space);
    if (index !== -1) {
      this.pinnedSpaces.splice(index, 1);
    }
  }

  addSvgTapListener() {
    const svgElement = this.props.triFloorPlan.svgElement;
    if (
      svgElement !== this.lastSvgElement &&
      this.props.onSelectedChange != null
    ) {
      if (svgElement != null) {
        svgElement.addEventListener("click", this.svgContainerTapped);
      }
      this.lastSvgElement = svgElement;
    }
  }

  removeSvgTapListener() {
    const svgElement = this.props.triFloorPlan.svgElement;
    if (svgElement != null && svgElement === this.lastSvgElement) {
      svgElement.removeEventListener("click", this.svgContainerTapped);
    }
    this.lastSvgElement = null;
  }

  svgContainerTapped = debounce(
    (e) => {
      const { enabled, floorPlanLoaded, onSelectedChange } = this.props;
      if (!enabled || !floorPlanLoaded) return;
      let selected = null;
      const triRecordId = e.target.getAttribute( this.idAttribute );

      if (triRecordId && e.target.getAttribute("pin") === "true") {
        selected = this.pins.find(
          (pin) =>
            TriFloorPlanUtils.getPropValue(pin, TriPinPlugin.SPACE_ID) ===
            triRecordId
        );

        if (
          selected != null &&
          this.props.selected &&
          TriFloorPlanUtils.getPropValue(selected, TriPinPlugin.SPACE_ID) ===
            TriFloorPlanUtils.getPropValue(
              this.props.selected,
              TriPinPlugin.SPACE_ID
            )
        ) {
          selected = null;
        }
      }

      if (onSelectedChange) {
        onSelectedChange(selected);
      }
    },
    300,
    { leading: true, trailing: false }
  );

  selectSvgPin(selected) {
    if (!selected) return;

    const spaceIndex = this.pinnedSpaces.findIndex(
      (space) =>
        space.getAttribute( this.idAttribute ) ===
        TriFloorPlanUtils.getPropValue(selected, TriPinPlugin.SPACE_ID)
    );
    if (spaceIndex !== -1) {
      this.pinnedSpaces[spaceIndex].pinIcon.classList.add(
        this.selectedClassName
      );
    }
  }

  deselectSvgPin(selected) {
    if (!selected) return;

    const spaceIndex = this.pinnedSpaces.findIndex(
      (space) =>
        space.getAttribute( this.idAttribute ) ===
        TriFloorPlanUtils.getPropValue(selected, TriPinPlugin.SPACE_ID)
    );
    if (spaceIndex !== -1) {
      this.pinnedSpaces[spaceIndex].pinIcon.classList.remove(
        this.selectedClassName
      );
    }
  }

  scalePinIconSizeAndCenter(pinIcon, space, scale, pinSize, centerPoints, location) {
    let centerX = 0;
    let centerY = 0;
    const spaceId = space.getAttribute( this.idAttribute );
    const pinScaledSize = pinSize * scale;

    pinIcon.setAttribute("width", pinScaledSize);
    pinIcon.setAttribute("height", pinScaledSize);

    /*
    if( location )
    {
      centerX = location.x;
      centerY = location.y;
    } else
    */
      if( centerPoints && centerPoints[ spaceId ] )
      {
      centerX = centerPoints[spaceId].x;
      centerY = centerPoints[spaceId].y;
    } else if (!space.points) {
      const spaceBBox = space.getBBox();
      centerX = spaceBBox.x + spaceBBox.width / 2;
      centerY = spaceBBox.y + spaceBBox.height / 2;
    } else {
      const centroid = TriFloorPlanUtils.calculateSpaceCentroid(space.points);
      centerX = centroid.x;
      centerY = centroid.y;
    }

    pinIcon.setAttribute("x", centerX - Math.floor(pinScaledSize / 2));
    pinIcon.setAttribute("y", centerY - Math.floor(pinScaledSize));
  }
}
